import { useCallback, useEffect, useMemo, useState } from "react";
import { defaultAxiosInstance, defaultAxiosInstanceV2 } from "../api/defaultInstance";
import "../css/AdManagement.css";
import CampaignPageLayout from "../modules/campaign/components/CampaignPageLayout";
import KeywordEditModal from "../modules/campaign/components/KeywordEditModal";
import NotificationModal from "../modules/campaign/components/NotificationModal";
import RefundModal from "../modules/campaign/components/RefundModal";
import adManagementConfig from "../modules/campaign/configs/adManagement.config";
import { useDateEdit } from "../modules/campaign/hooks/useDateEdit";
import { useFilters } from "../modules/campaign/hooks/useFilters";
import { useKeywordEdit } from "../modules/campaign/hooks/useKeywordEdit";
import { useSelection } from "../modules/campaign/hooks/useSelection";
import { action, submitRefund, saveMemo } from "../modules/campaign/services/campaignService";
import { executeCancel, pointPaymentCancel } from "../modules/wallet/services/walletService";
import { exportAdManagementXlsx } from "../modules/campaign/services/exporters/adManagementExporter";
import MemoModal from "../modules/campaign/components/MemoModal";
import { useMemoEdit } from "../modules/campaign/hooks/useMemoEdit";
import Layout from "./Layout";

function AdManagement() {
  const filters = useFilters(adManagementConfig.filters.default);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    totalCount: 0,
    waitingCount: 0,
    activeCount: 0,
    expiringSoonCount: 0,
    terminatedCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const [refundModalData, setRefundModalData] = useState(null);

  const openNotificationModal = (data) => {
    if (!data.adCampaignMetaId) {
      alert("캠페인 메타 ID가 없습니다.");
      return;
    }
    setNotificationData(data);
  };

  const closeNotificationModal = () => {
    setNotificationData(null);
  };

  const handleRefundOpen = (selectedIds, allItems) => {
    const keyField = adManagementConfig.table.keyField;
    const selectedItems = allItems
      .filter(item => selectedIds.includes(item[keyField]) && (item.paid === "complete" || (item.paid === "refund" && (item.remainingAmount ?? 0) > 0)))
      .map(item => ({
        adCampaignMetaId: item.adCampaignMetaId,
        userName: item.userName,
        userId: item.userId,
        paymentType: item.paymentType,
        adPaymentTransactionId: item.adPaymentTransactionId,
        totalAmount: item.totalAmount ?? item.totalCost ?? 0,
        refundedAmount: item.refundedAmount ?? 0,
        remainingAmount: item.remainingAmount ?? item.totalAmount ?? item.totalCost ?? 0,
      }));
    const hasPoint = selectedItems.some(i => i.paymentType === 'point');
    if (hasPoint && selectedItems.length > 1) {
      alert('포인트 결제 건이 포함된 경우 1건씩 환불해주세요.');
      return;
    }
    setRefundModalData({ items: selectedItems });
  };

  const handleRefundSubmit = async (refundItems, reason = '환불 처리') => {
    try {
      const nonPointItems = refundItems.filter(i => i.paymentType !== 'point');
      const pointItems = refundItems.filter(i => i.paymentType === 'point' && i.adPaymentTransactionId);

      if (nonPointItems.length > 0) {
        await submitRefund(adManagementConfig, nonPointItems);
      }

      const txGroups = {};
      for (const item of pointItems) {
        const txId = item.adPaymentTransactionId;
        if (!txGroups[txId]) txGroups[txId] = { userId: item.userId, items: [] };
        txGroups[txId].items.push({ adCampaignMetaId: item.adCampaignMetaId, cancelAmount: item.cancelAmount });
      }
      for (const [txId, { userId, items: cancelItems }] of Object.entries(txGroups)) {
        await executeCancel(Number(txId), cancelItems, reason);
      }
      setRefundModalData(null);
      selection.clearSelection();
      await fetchData();
      alert("환불 처리가 완료되었습니다.");
    } catch (error) {
      console.error("환불 처리 오류:", error);
      alert(error.message || "환불 처리 중 오류가 발생했습니다.");
    }
  };

  // useSelection은 items가 변경될 때마다 업데이트되도록 items를 전달
  const selection = useSelection(items, adManagementConfig.table.keyField);
  const memoEdit = useMemoEdit({
    saveFn: saveMemo,
    onUpdate: (id, newMemo) => {
      setItems((prev) =>
        prev.map((item) =>
          item.adCampaignMetaId === id ? { ...item, memo: newMemo } : item,
        ),
      );
    },
  });
  // 데이터 조회
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // "전체", 빈 문자열, 빈 배열은 파라미터에서 제외 (백엔드가 인식 못함)
      const cleanParams = Object.fromEntries(
        Object.entries(filters.filter).filter(([_, v]) => {
          if (v === '' || v === '전체') return false;
          if (Array.isArray(v) && v.length === 0) return false;
          return true;
        })
      );
      // count API는 page/limit 불필요
      const { page: _page, limit: _limit, ...countParams } = cleanParams;

      // list와 count를 독립적으로 호출 (count 실패해도 list는 표시)
      const listResponse = await defaultAxiosInstanceV2.get("/advertisement/admin/campaigns", {
        params: cleanParams,
      });
      const listResult = listResponse.data.data;
      setItems(listResult.metaCampaigns || []);

      try {
        const countResponse = await defaultAxiosInstanceV2.get("/advertisement/admin/campaigns/count", {
          params: countParams,
        });
        const countResult = countResponse.data.data;
        setSummary({
          totalCount: countResult.totalCount || 0,
          waitingCount: countResult.waitingCount || 0,
          activeCount: countResult.activeCount || 0,
          expiringSoonCount: countResult.expiringSoonCount || 0,
          terminatedCount: countResult.terminatedCount || 0,
        });
      } catch (countError) {
        console.warn('count API 실패, 목록 총 건수로 대체:', countError?.message);
        setSummary((prev) => ({ ...prev, totalCount: listResult.totalCount || 0 }));
      }
    } catch (error) {
      console.error("데이터 조회 오류:", error);
    } finally {
      setLoading(false);
    }
  }, [filters.filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleNotificationClick = (e) => {
      const button = e.target.closest('button[data-action="notification"]');
      if (button) {
        const adCampaignMetaId = Number(button.getAttribute("data-campaign-meta-id"));
        const userId = Number(button.getAttribute("data-user-id"));
        const productName = button.getAttribute("data-product-name") || "";
        const submittedAt = button.getAttribute("data-submitted-at") || "";
        const purchaseAmount = Number(button.getAttribute("data-purchase-amount")) || 0;
        openNotificationModal({ adCampaignMetaId, userId, productName, submittedAt, purchaseAmount });
      }
    };

    document.addEventListener("click", handleNotificationClick, true);

    return () => {
      document.removeEventListener("click", handleNotificationClick, true);
    };
  }, []);

  useEffect(() => {
    const handleMemoClick = (e) => {
      const button = e.target.closest('button[data-action="memo"]');
      if (button) {
        const adCampaignMetaId = Number(button.getAttribute("data-campaign-meta-id"));
        const currentMemo = button.getAttribute("data-memo") || null;
        memoEdit.openModal(adCampaignMetaId, currentMemo || null);
      }
    };
    document.addEventListener("click", handleMemoClick, true);
    return () => document.removeEventListener("click", handleMemoClick, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoEdit.openModal]);

  // 키워드 수정 훅
  const keywordEdit = useKeywordEdit(fetchData);

  // 날짜 수정 훅
  const dateEdit = useDateEdit("/advertisement/admin/campaign/date", fetchData);

  // 행 클릭 핸들러 (키워드 모달 열기)
  const handleRowClick = useCallback(
    (row) => {
      keywordEdit.openModal(row);
    },
    [keywordEdit],
  );

  // rowClick 핸들러를 config에 주입
  const configWithRowClick = useMemo(
    () => ({
      ...adManagementConfig,
      table: {
        ...adManagementConfig.table,
        rowClick: {
          enabled: true,
          handler: handleRowClick,
        },
      },
    }),
    [handleRowClick],
  );

  // 날짜 편집 모드를 포함한 컬럼 생성
  const columnsWithDateEdit = useMemo(() => {
    return configWithRowClick.table.columns.map((column) => {
      if (column.key === "startDate") {
        return {
          ...column,
          render: dateEdit.createDateColumnRenderer(),
        };
      }
      return column;
    });
  }, [configWithRowClick.table.columns, dateEdit]);

  const configWithDateEdit = useMemo(
    () => ({
      ...configWithRowClick,
      table: {
        ...configWithRowClick.table,
        columns: columnsWithDateEdit,
      },
    }),
    [columnsWithDateEdit, configWithRowClick],
  );

  // 필터 핸들러
  const handleFilterChange = (name, value) => {
    filters.updateFilter(name, value);
  };

  const handleFilterApply = () => {
    filters.applyFilter();
    selection.clearSelection();
  };

  const handleFilterReset = () => {
    filters.resetFilter();
    selection.clearSelection();
  };

  // 선택 핸들러
  const handleSelect = (id) => {
    selection.toggleId(id);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      selection.selectAll();
    } else {
      selection.clearSelection();
    }
  };

  // 페이지 핸들러
  const handlePageChange = (page) => {
    filters.setPage(page);
  };

  // 액션 핸들러
  const handleAction = async (actionType, payload, actionConfig) => {
    try {
      let result;

      if (actionType === "terminate") {
        // 종료는 DELETE 메서드 사용
        const response = await defaultAxiosInstance.delete("/advertisement/admin/campaign", {
          params: {
            adCampaignMetaIds: payload.adCampaignMetaIds,
            toggleValue: payload.toggleValue,
          },
        });
        result = {
          status: response.data.status === 200 ? "success" : "error",
          raw: response,
        };
      } else {
        result = await action(adManagementConfig, actionType, payload);
      }

      if (result.status === "success") {
        let message = "처리되었습니다.";
        if (actionType === "submitted") {
          message = payload.toggleValue === 1 ? "접수 완료 처리되었습니다." : "접수 취소 처리되었습니다.";
        } else if (actionType === "paid") {
          message = "결제 완료 처리되었습니다.";
        } else if (actionType === "refund") {
          message = "환불 처리되었습니다.";
        } else if (actionType === "terminate") {
          message = payload.toggleValue === 1 ? "종료 처리되었습니다." : "취소 처리되었습니다.";
        } else if (actionType === "delete") {
          message = "삭제되었습니다.";
        }
        alert(message);

        selection.clearSelection();
        await fetchData();
      } else {
        alert("처리 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error("액션 처리 오류:", error);
      alert(error.message || "처리 중 오류가 발생했습니다.");
    }
  };

  // 다운로드 핸들러
  const handleDownload = async (selectedIds, allItems) => {
    try {
      setIsDownloading(true);

      const dataToExport =
        selectedIds.length > 0
          ? allItems.filter((item) => selectedIds.includes(item[adManagementConfig.table.keyField]))
          : allItems;

      if (dataToExport.length === 0) {
        alert("다운로드할 데이터가 없습니다.");
        return;
      }

      await exportAdManagementXlsx(dataToExport);
      alert("다운로드가 완료되었습니다.");
    } catch (error) {
      console.error("다운로드 오류:", error);
      alert("다운로드 중 오류가 발생했습니다.");
    } finally {
      setIsDownloading(false);
    }
  };

  // 키워드 클릭 이벤트 리스너 (날짜 클릭은 useDateEdit 훅에서 직접 처리)
  useEffect(() => {
    const handleKeywordSpanClick = (e) => {
      const span = e.target.closest("span[data-keyword]");
      if (span && !keywordEdit.showModal) {
        const keyword = span.getAttribute("data-keyword");
        if (keyword) {
          e.stopPropagation();
          const searchUrl = `https://m.search.naver.com/search.naver?query=${encodeURIComponent(keyword)}&sm=mtp_hty.top&where=m`;
          window.open(searchUrl, "_blank");
        }
      }
    };

    document.addEventListener("click", handleKeywordSpanClick);

    return () => {
      document.removeEventListener("click", handleKeywordSpanClick);
    };
  }, [keywordEdit.showModal]);

  return (
    <Layout>
<CampaignPageLayout
        config={configWithDateEdit}
        data={{ items, summary }}
        filter={filters.filter}
        selectedIds={selection.selectedIds}
        loading={loading || isDownloading}
        handlers={{
          tempFilter: filters.tempFilter,
          onFilterChange: handleFilterChange,
          onFilterApply: handleFilterApply,
          onFilterReset: handleFilterReset,
          onSelect: handleSelect,
          onSelectAll: handleSelectAll,
          onPageChange: handlePageChange,
          onAction: handleAction,
          onDownload: handleDownload,
          onRefundOpen: handleRefundOpen,
        }}
      />

      {/* 키워드 수정 모달 */}
      <KeywordEditModal
        isOpen={keywordEdit.showModal}
        modalData={keywordEdit.modalData}
        changes={keywordEdit.changes}
        loading={keywordEdit.loading}
        onKeywordChange={keywordEdit.handleChange}
        onAddNew={keywordEdit.handleAddNew}
        onUpdateNew={keywordEdit.updateNewKeyword}
        onRemoveNew={keywordEdit.removeNewKeyword}
        onKeywordClick={keywordEdit.openKeywordSearch}
        onSave={keywordEdit.save}
        onClose={keywordEdit.closeModal}
        getChangeForKeyword={keywordEdit.getChangeForKeyword}
        getNewKeywordsForCampaign={keywordEdit.getNewKeywordsForCampaign}
      />

      <NotificationModal
        isOpen={notificationData !== null}
        adCampaignMetaId={notificationData?.adCampaignMetaId}
        userId={notificationData?.userId}
        productName={notificationData?.productName}
        submittedAt={notificationData?.submittedAt}
        purchaseAmount={notificationData?.purchaseAmount}
        onClose={closeNotificationModal}
        onSuccess={fetchData}
      />

      <RefundModal
        isOpen={refundModalData !== null}
        items={refundModalData?.items || []}
        onClose={() => setRefundModalData(null)}
        onSubmit={handleRefundSubmit}
      />

      <MemoModal
        isOpen={memoEdit.isOpen}
        id={memoEdit.memoData?.id}
        currentMemo={memoEdit.memoData?.currentMemo}
        loading={memoEdit.loading}
        onSave={memoEdit.save}
        onDelete={memoEdit.deleteMemo}
        onClose={memoEdit.closeModal}
      />
    </Layout>
  );
}

export default AdManagement;
